import React from "react"
import { Link, useLocation } from "react-router-dom"
import { Box, Flex, Text, Separator, IconButton } from "@radix-ui/themes"
import { LucideIcon, LogOut, X } from "lucide-react"
import TurboLogo from "../../TurboLogo"

interface MenuItem {
  path: string
  icon: LucideIcon | React.FC<{ className?: string; size?: number }>
  label: string
  badge?: number
}

interface UnifiedSidebarProps {
  menuItems: MenuItem[]
  title: string
  subtitle: string
  onLogout?: () => void
  onClose?: () => void
  logoColor?: string
}

const UnifiedSidebar: React.FC<UnifiedSidebarProps> = ({
  menuItems,
  title,
  subtitle,
  onLogout,
  onClose,
  logoColor = "#DC2626"
}) => {
  const location = useLocation()

  const handleLogout = () => {
    if (onLogout) {
      onLogout()
    }
  }

  const handleNavClick = () => {
    // Close sidebar on mobile when navigation item is clicked
    if (onClose) {
      onClose()
    }
  }

  return (
    <Box
      className="w-64 min-h-screen"
      style={{
        backgroundColor: 'var(--color-background)',
        boxShadow: 'var(--shadow-4)'
      }}
    >
      <Box p="6">
        <Flex align="center" justify="between">
          <Flex align="center" gap="3">
            <TurboLogo width={42} height={36} color={logoColor} />
            <Flex direction="column" gap="0">
              <Text size="4" weight="bold" style={{ lineHeight: 1.2 }}>{title}</Text>
              <Text size="1" color="gray" style={{ lineHeight: 1.2, marginTop: '-2px' }}>{subtitle}</Text>
            </Flex>
          </Flex>

          {/* Mobile close button */}
          {onClose && (
            <IconButton
              variant="ghost"
              size="2"
              onClick={onClose}
              className="lg:hidden"
            >
              <X className="w-5 h-5" />
            </IconButton>
          )}
        </Flex>
      </Box>

      <Box asChild>
        <nav>
          <Box px="3">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              // @ts-ignore - badge might be dynamic
              const badgeCount = item.badge

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleNavClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    marginBottom: '4px',
                    borderRadius: 'var(--radius-3)',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s',
                    backgroundColor: isActive ? 'var(--accent-3)' : 'transparent',
                    color: isActive ? 'var(--accent-11)' : 'var(--gray-11)',
                    position: 'relative'
                  }}
                  className="hover:bg-[var(--accent-2)]"
                >
                  <Icon className="w-5 h-5" style={{ marginRight: '12px' }} />
                  <Text size="2" weight={isActive ? "medium" : "regular"}>
                    {item.label}
                  </Text>
                  {badgeCount && badgeCount > 0 ? (
                    <span
                      className="absolute right-2 px-1.5 py-0.5 min-w-[20px] text-center rounded-full bg-red-600 text-white text-[11px] font-bold animate-pulse"
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  ) : null}
                </Link>
              )
            })}
          </Box>

          {onLogout && (
            <Box px="3" mt="8" pt="8">
              <Separator size="4" mb="4" />
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-3)',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: 'var(--gray-11)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-[var(--gray-3)]"
              >
                <LogOut className="w-5 h-5" style={{ marginRight: '12px' }} />
                <Text size="2">Logout</Text>
              </button>
            </Box>
          )}
        </nav>
      </Box>
    </Box>
  )
}

export default UnifiedSidebar 